import { useState, useEffect } from "react";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Renders a client logo with a WebP <picture> fallback when the source is a
// local raster image. Kept as a top-level component (not inline) so the
// <source> element remains an unconditional direct child of <picture> —
// otherwise dev-mode JSX instrumentation wraps it in a <span> and browsers
// silently fall back to the heavier PNG. See /app/frontend/src/components/LazyImage.jsx.
function LogoImg({ src, alt }) {
  const isLocalRaster = typeof src === "string" && src.startsWith("/") && /\.(png|jpe?g)$/i.test(src);
  const webpSrc = isLocalRaster ? src.replace(/\.(png|jpe?g)$/i, ".webp") : null;
  // Inline the <img> literally in both branches — variable interpolation gets
  // wrapped in a <span> by dev-mode instrumentation, which breaks <picture>.
  if (webpSrc) {
    return (
      <picture>
        <source srcSet={webpSrc} type="image/webp" />
        <img
          src={src}
          alt={alt}
          className="max-h-12 max-w-full object-contain"
          loading="lazy"
          decoding="async"
        />
      </picture>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="max-h-12 max-w-full object-contain"
      loading="lazy"
      decoding="async"
    />
  );
}

const defaultLogos = [
  { name: "Prima Plastics", logo: "/clientele/prima-plastics.png" },
  { name: "Oswal Industries", logo: "/clientele/oswal-industries.png" },
  { name: "Allied Digital", logo: "/clientele/allied-digital.png" },
  { name: "Solitaire Overseas", logo: "/clientele/solitaire-overseas.png" },
  { name: "Fire Fite (Bharati Fire Engineers)", logo: "/clientele/fire-fite.png" },
  { name: "Gillette", logo: "/clientele/gillete.png" },
  { name: "P&G", logo: "/clientele/p-g.png" },
  { name: "Awfis", logo: "/clientele/awfis.png" },
  { name: "D2H", logo: "/clientele/D2H.jpg" },
  { name: "Ion Exchange", logo: "/clientele/ION-EXCHANGE.jpg" },
  { name: "Safexpress", logo: "/clientele/safexpress-logo.png" },
  { name: "T-Series", logo: "/clientele/t-series-logo.png" },
  { name: "Ultratech Cement", logo: "/clientele/Ultratech-Cement.jpg" },
  { name: "Indian Bank", logo: "/clientele/indianb.jpg" },
  { name: "L&T", logo: "/clientele/L-T-logo.jpg" },
  { name: "HPCL", logo: "/clientele/HPCL-Hindustan-Petroleum-Corporation.jpg" },
  { name: "Mother Dairy", logo: "/clientele/mother-dairy.jpg" },
  { name: "Wizcraft", logo: "/clientele/WIZCRAFT.jpg" },
  { name: "NHAVA", logo: "/clientele/nhava.png" },
  { name: "Witty", logo: "/clientele/witty.png" },
  { name: "Blenheim", logo: "/clientele/blenheim.jpg" },
];

const defaultContent = {
  sectionTitle: "Our Clientele",
  items: defaultLogos
};

export default function ClientLogos() {
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content/clientLogos`);
        if (response.data && response.data.items?.length > 0) {
          setContent({ ...defaultContent, ...response.data });
        }
      } catch (error) {
        console.log("Using default client logos");
      }
    };
    fetchContent();
  }, []);

  return (
    <section className="py-20 bg-[#F8FAFC] border-t border-b border-[#E2E8F0]" data-testid="client-logos-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h2 className="text-3xl font-bold text-[#0F172A]">
            {content.sectionTitle}
          </h2>
        </motion.div>
      </div>

      <div className="logo-marquee-container">
        <Marquee
          speed={40}
          gradient={false}
          pauseOnHover
          className="py-8"
        >
          {content.items?.map((client, index) => (
            <div
              key={`${client.name}-${index}`}
              className="mx-8 flex items-center justify-center"
            >
              <div className="bg-white border border-[#E2E8F0] p-4 h-20 w-40 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300">
                <LogoImg
                  src={client.logo || client.url}
                  alt={`${client.name} - Trusted corporate client of Ezeefit Office Furniture Mumbai`}
                />
              </div>
            </div>
          ))}
        </Marquee>
      </div>

      <div className="logo-marquee-container mt-4">
        <Marquee
          speed={30}
          gradient={false}
          pauseOnHover
          direction="right"
          className="py-8"
        >
          {[...(content.items || [])].reverse().map((client, index) => (
            <div
              key={`${client.name}-reverse-${index}`}
              className="mx-8 flex items-center justify-center"
            >
              <div className="bg-white border border-[#E2E8F0] p-4 h-20 w-40 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300">
                <LogoImg
                  src={client.logo || client.url}
                  alt={`${client.name} - Corporate office furniture client of Ezeefit Mumbai`}
                />
              </div>
            </div>
          ))}
        </Marquee>
      </div>
    </section>
  );
}
